import { useEffect, useState } from 'react';
import { toolMeta } from '@/lib/toolmeta';
import { DEMO } from '@/api/config';
import { bundledIcon, faviconServiceUrl, toolDomain } from '@/lib/toolicons';
import { loadBrandIcon, markInitials, markTint, type BrandIcon } from '@/lib/brandIcon';
import { cn } from '@/lib/cn';
import type { ToolDisplay } from '@/api/backend';

/**
 * Open-world tool icon resolution, in four steps, each more of a guess than
 * the last and none of them inventing what a tool is:
 *
 *   1. a bundled mark, for the few the set withdrew (Slack) and the generic
 *      detectors that are not brands at all
 *   2. a mark fetched by slug, derived from the backend's iconSlug or tool id,
 *      so a tool added to the registry today draws without a frontend deploy
 *   3. the favicon of the domain the backend declared, if it declared one
 *   4. the tool's own initials, product first, in a stable tint
 *
 * The backend's display metadata is the only identity authority; step 2 only
 * looks for a picture to go with a name it already committed to. toolMeta is
 * demo-fixture presentation only.
 */
export function ToolIcon({ tool, size = 16, className, display }: { tool: string; size?: number; className?: string; display?: ToolDisplay }) {
  const [broken, setBroken] = useState(false);
  // Marks the backend can name but this build was never told about resolve
  // from a split-out module, so a tool added to the registry today still draws
  // correctly. See lib/brandIcon.
  const [brand, setBrand] = useState<BrandIcon | null>(null);
  const slugHint = display?.iconSlug ?? null;
  useEffect(() => {
    let live = true;
    void loadBrandIcon(tool, slugHint).then((b) => {
      if (live) setBrand(b);
    });
    return () => {
      live = false;
    };
  }, [tool, slugHint]);

  const icon = bundledIcon(display?.iconSlug) ?? bundledIcon(tool);
  if (icon) {
    return (
      <svg aria-hidden="true" width={size} height={size} viewBox={icon.viewBox} className={cn('flex-none', className)}>
        {icon.paths.map((p, i) => (
          <path key={i} d={p.d} fill={p.fill} />
        ))}
      </svg>
    );
  }
  if (brand) {
    return (
      <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" className={cn('flex-none', className)}>
        <path d={brand.path} fill={`#${brand.hex}`} />
      </svg>
    );
  }

  // The backend declares no domain today, so a table of public facts about
  // public software stands in - one service for all of them, so a row never
  // looks different from its neighbour for reasons that are not about the tool.
  const domain = display?.domain ?? toolDomain(tool) ?? (DEMO ? toolMeta(tool).domain : null);
  if (domain && !broken) {
    return (
      <img
        src={faviconServiceUrl(domain)}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        className={cn('flex-none rounded-sm', className)}
        style={{ width: size, height: size }}
      />
    );
  }
  // No mark exists, so draw the tool's own initials rather than the vendor's:
  // Word, Excel, PowerPoint and Office all begin "Microsoft" and were drawing
  // the same grey M in the list meant to tell them apart.
  const label = display?.label ?? (DEMO ? toolMeta(tool).label : tool);
  const initials = markInitials(label, tool);
  const color = display?.brandColor
    ? display.brandColor
    : DEMO
      ? `#${toolMeta(tool).color}`
      : markTint(tool);
  return (
    <span
      aria-hidden="true"
      title={label}
      className={cn('grid flex-none place-items-center rounded-sm font-semibold', className)}
      style={{
        width: size,
        height: size,
        background: `color-mix(in srgb, ${color} 16%, transparent)`,
        color,
        fontSize: Math.max(8, Math.round(size * (initials.length > 1 ? 0.42 : 0.56))),
        lineHeight: 1,
      }}
    >
      {initials}
    </span>
  );
}
